import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FilePlus2Icon, XIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export type InvoiceProjectOption = {
  id: string;
  ref: string;
  name: string;
  client: string;
  departments: string[];
};
export type NewInvoiceDraft = {
  projectId: string;
  department: string;
  category: 'Interim' | 'Final' | 'Credit note' | 'Other';
  paymentStatus: 'paid' | 'unpaid' | 'cancelled';
  value: number;
  date: string;
  sageId?: string;
  description?: string;
};
type NewInvoiceDialogProps = {
  projects: InvoiceProjectOption[];
  onClose: () => void;
  onCreate: (invoice: NewInvoiceDraft) => void;
};
const INVOICE_CATEGORIES: NewInvoiceDraft['category'][] = ['Interim', 'Final', 'Credit note', 'Other'];
export function NewInvoiceDialog({
  projects,
  onClose,
  onCreate
}: NewInvoiceDialogProps) {
  const initialProject = projects[0];
  const [projectId, setProjectId] = useState(initialProject?.id || '');
  const [department, setDepartment] = useState(initialProject?.departments[0] || '');
  const [category, setCategory] = useState<NewInvoiceDraft['category']>('Interim');
  const [paymentStatus, setPaymentStatus] = useState<NewInvoiceDraft['paymentStatus']>('unpaid');
  const [value, setValue] = useState('');
  const [date, setDate] = useState(todayDate());
  const [sageId, setSageId] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const projectSelectRef = useRef<HTMLSelectElement>(null);
  const selectedProject = useMemo(() => projects.find(project => project.id === projectId), [projectId, projects]);
  const numericValue = Number(value);
  const isCreditNote = category === 'Credit note';
  const canSubmit = Boolean(projectId && department && value.trim() && Number.isFinite(numericValue) && numericValue !== 0 && date);
  useEffect(() => {
    projectSelectRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  useEffect(() => {
    if (!projects.some(project => project.id === projectId)) {
      setProjectId(projects[0]?.id || '');
    }
  }, [projectId, projects]);
  useEffect(() => {
    const nextDepartments = selectedProject?.departments || [];
    if (!nextDepartments.includes(department)) {
      setDepartment(nextDepartments[0] || '');
    }
  }, [department, selectedProject]);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) {
      setError('Complete the project, department, value and date to continue.');
      return;
    }
    onCreate({
      projectId,
      department,
      category,
      paymentStatus: isCreditNote ? 'paid' : paymentStatus,
      value: isCreditNote ? -Math.abs(numericValue) : Math.abs(numericValue),
      date,
      sageId: sageId.trim() || undefined,
      description: description.trim() || undefined
    });
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-20" data-mp="tdfbqp8jdxj5">
      <button type="button" onClick={onClose} className="absolute inset-0 cursor-default bg-slate-900/45 backdrop-blur-sm" aria-label="Close new invoice dialog" data-id="element-21" data-mp="tbb17og4u09w" />
      <motion.section initial={{
      opacity: 0,
      scale: 0.97,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.97,
      y: 10
    }} transition={{
      duration: 0.18,
      ease: 'easeOut'
    }} className="relative w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="new-invoice-title" aria-describedby="new-invoice-dialog-description" data-id="element-22" data-mp="174k3z69krnjg">
        <header className="flex items-start justify-between border-b border-slate-100 p-5" data-id="element-23" data-mp="3iw7b6kw1hifw">
          <div className="flex items-start gap-3" data-id="element-24" data-mp="1d3zn9q96tcyz">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[#14466B]" data-id="element-25" data-mp="187ix6qifgoj9">
              <FilePlus2Icon className="h-5 w-5" data-id="element-26" data-mp="1m5z1kasti2y8" />
            </span>
            <div data-id="element-27" data-mp="1h75yzdf2jwcu">
              <h2 id="new-invoice-title" className="text-lg font-bold text-slate-900" data-id="element-28" data-mp="1h8pg168jubfo">
                New invoice
              </h2>
              <p id="new-invoice-dialog-description" className="mt-1 text-sm text-slate-500" data-id="element-29" data-mp="ph16ssl2i512">
                Assign the invoice to a project and department.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-100" aria-label="Close new invoice dialog" data-id="element-30" data-mp="yetqe8xtnyu9">
            <XIcon className="h-5 w-5" data-id="element-31" data-mp="2hdrkemw40kgy" />
          </button>
        </header>

        <form onSubmit={handleSubmit} data-id="element-32" data-mp="2m7m0lgciahba">
          <div className="space-y-5 p-5" data-id="element-33" data-mp="28f64z0lxplke">
            <div data-id="element-34" data-mp="28g7f92v4m412">
              <label htmlFor="new-invoice-project" className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-35" data-mp="28gqycmwiiygo">
                Project <span className="text-rose-600" data-id="element-36" data-mp="314mvtttwouei">*</span>
              </label>
              <select ref={projectSelectRef} id="new-invoice-project" value={projectId} onChange={event => {
              const nextProject = projects.find(project => project.id === event.target.value);
              setProjectId(event.target.value);
              setDepartment(nextProject?.departments[0] || '');
              setError(null);
            }} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-37" data-mp="355owmf3h1eil">
                {projects.map(project => <option key={project.id} value={project.id} data-id="element-38" data-mp="rsss44cachhc">
                    {project.ref} · {project.name}
                  </option>)}
              </select>
              {selectedProject && <p className="mt-1.5 text-xs text-slate-500" data-id="element-39" data-mp="cxmotp4kqwrp">
                  {selectedProject.client}
                </p>}
            </div>

            <div className="grid gap-4 sm:grid-cols-2" data-id="element-40" data-mp="8wkw7r55xiyp">
              <div data-id="element-41" data-mp="3xhqe4hcz8po">
                <label htmlFor="new-invoice-department" className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-42" data-mp="3xzutoe6ct8o">
                  Department <span className="text-rose-600" data-id="element-43" data-mp="1yag1uresobel">*</span>
                </label>
                <select id="new-invoice-department" value={department} onChange={event => {
                setDepartment(event.target.value);
                setError(null);
              }} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-44" data-mp="25uial0jxjxbr">
                  {selectedProject?.departments.map(item => <option key={item} value={item} data-id="element-45" data-mp="1rvikdogrradh">
                      {item}
                    </option>)}
                </select>
              </div>
              <div data-id="element-46" data-mp="20vnqlva882q2">
                <label htmlFor="new-invoice-category" className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-47" data-mp="20w6y0i2938pu">
                  Category
                </label>
                <select id="new-invoice-category" value={category} onChange={event => {
                setCategory(event.target.value as NewInvoiceDraft['category']);
                setError(null);
              }} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-48" data-mp="3c8r9dqzpm9py">
                  {INVOICE_CATEGORIES.map(item => <option key={item} value={item} data-id="element-49" data-mp="fywnnqbfat5u">
                      {item}
                    </option>)}
                </select>
              </div>
              <div data-id="element-50" data-mp="pwb0lfy50zte">
                <label htmlFor="new-invoice-value" className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-51" data-mp="pw1c5wsqr0pt">
                  Value <span className="text-rose-600" data-id="element-52" data-mp="2rnnvrdaaztc9">*</span>
                </label>
                <input id="new-invoice-value" type="number" min="0" step="0.01" value={value} onChange={event => {
                setValue(event.target.value);
                setError(null);
              }} placeholder="0.00" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-53" data-mp="2rpqwosczv6vr" />
                {isCreditNote && <p className="mt-1.5 text-xs text-rose-600" data-id="element-54" data-mp="eh7qoum0q895">
                    Saved as a negative value.
                  </p>}
              </div>
              <div data-id="element-55" data-mp="jgaukbq9i8fj">
                <label htmlFor="new-invoice-date" className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-56" data-mp="28uz9okq8m4h9">
                  Invoice date <span className="text-rose-600" data-id="element-57" data-mp="2drzfqvn3778d">*</span>
                </label>
                <input id="new-invoice-date" type="date" value={date} onChange={event => {
                setDate(event.target.value);
                setError(null);
              }} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-58" data-mp="2qqqdctgo82ji" />
              </div>
            </div>

            <div data-id="element-59" data-mp="3efrmkixbvhpu">
              <label htmlFor="new-invoice-description" className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-60" data-mp="30i323c76aclb">
                Description <span className="text-slate-400" data-id="element-61" data-mp="35gebxmxgwn3j">(optional)</span>
              </label>
              <textarea id="new-invoice-description" value={description} onChange={event => setDescription(event.target.value)} rows={2} placeholder="Add a short description for this invoice" className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-62" data-mp="1p2eh2qdhbnn5" />
            </div>

            <div className="grid gap-4 sm:grid-cols-2" data-id="element-63" data-mp="1vjg1o91kxjnh">
              <div data-id="element-64" data-mp="1vgtu49eov459">
                <label htmlFor="new-invoice-sage-id" className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-65" data-mp="1qkcuahy4n1cc">
                  Sage ID <span className="text-slate-400" data-id="element-66" data-mp="12tqufdya0ndf">(optional)</span>
                </label>
                <input id="new-invoice-sage-id" type="text" value={sageId} onChange={event => setSageId(event.target.value)} placeholder="e.g. SI-2026-001" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-67" data-mp="ytq713t791u8" />
              </div>
              <div data-id="element-68" data-mp="k1oe63pjr93p">
              <label htmlFor="new-invoice-status" className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-69" data-mp="f57mx0n1weme">
                Payment status
              </label>
              <select id="new-invoice-status" value={isCreditNote ? 'paid' : paymentStatus} disabled={isCreditNote} onChange={event => setPaymentStatus(event.target.value as NewInvoiceDraft['paymentStatus'])} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500" data-id="element-70" data-mp="2k5qsv13jbls0">
                <option value="unpaid" data-id="element-71" data-mp="2a9fhcdbrqzym">Outstanding</option>
                <option value="paid" data-id="element-72" data-mp="3hmfuatokom0v">Paid</option>
                <option value="cancelled" data-id="element-73" data-mp="3hnqy2ti0ptrz">Cancelled</option>
              </select>
              </div>
            </div>

            {error && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert" data-id="element-74" data-mp="1a0ftub1mrzdg">
                {error}
              </p>}
          </div>

          <footer className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 p-5" data-id="element-75" data-mp="1172pmcy49bc2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-76" data-mp="w9b01pky21b1">
              Cancel
            </button>
            <button type="submit" disabled={!canSubmit} className="inline-flex items-center gap-2 rounded-lg bg-[#14466B] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#0F3D5C] focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400" data-id="element-77" data-mp="i905ockfe4l4">
              <FilePlus2Icon className="h-4 w-4" data-id="element-78" data-mp="14axxssp4aoc8" />
              Create invoice
            </button>
          </footer>
        </form>
      </motion.section>
    </div>;
}
function todayDate() {
  return new Date().toISOString().slice(0, 10);
}